import { useCallback, useEffect, useState } from 'react';
import {
  BrandMark,
  Button,
  Field,
  Icon,
  Input,
  Tag,
  useErrorDialog,
} from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';
import './sign-in.css';

/**
 * Accepting an invite — the screen a handed-over link opens.
 *
 * HOW THE LINK GOT HERE, AND WHY THE PAGE DOES NOT GUESS. A Super Admin
 * generated it; the platform records an invite message carrying it, and whether
 * that message was delivered depends on how the environment is configured for
 * mail. This page has no way to know which — the person may have arrived from
 * an email or from a chat message pasted by hand — so it says what is true in
 * both cases and claims neither.
 *
 * THE TOKEN IS IN THE FRAGMENT. `#token=…` is never sent to a server, so the
 * credential does not land in an access log, a proxy log, or a Referer header
 * on the way here. It is read out of `location.hash`, posted in a body, and
 * then STRIPPED from the address bar so it does not sit in browser history for
 * whoever borrows the laptop next.
 *
 * ONE STEP SETS BOTH CREDENTIALS. The password and the authenticator are
 * confirmed in the same transaction, for every role and not only the ones that
 * mandate a factor, because "2FA must be set up before the first sign-in" is
 * what the invite dialog has always promised. It also means a newly accepted
 * admin is never in the unenrolled state that a role change into a money role
 * is refused for.
 *
 * NO SESSION IS MINTED HERE. Accepting proves you hold the link and the
 * authenticator; signing in is a separate act with its own attempt record and
 * its own lockout counter, and skipping it would skip both.
 */

interface Preview {
  email: string;
  displayName: string;
  role: string;
  expiresAt: string;
  roleRequiresTwoFactor: boolean;
  twoFactor: { uri: string; secret: string };
  passwordMinimum: number;
}

/** Read the token out of the fragment, then take it out of the address bar. */
function takeTokenFromFragment(): string | null {
  const raw = window.location.hash.replace(/^#/, '');
  const token = new URLSearchParams(raw).get('token');
  if (token) {
    window.history.replaceState(null, '', window.location.pathname);
  }
  return token;
}

export function AcceptInvite() {
  const { showError } = useErrorDialog();

  const [token] = useState<string | null>(() => takeTokenFromFragment());
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refused, setRefused] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<{ displayName: string; role: string; recoveryCodes: string[] } | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setRefused(
        'This address carries no invite token. Open the link exactly as it was given to you — ' +
          'the token sits after the # and is easy to lose when a link is retyped.',
      );
      setLoading(false);
      return;
    }
    api
      .post<Preview>('/api/auth/invite/preview', { token })
      .then((d) => {
        if (!cancelled) setPreview(d);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        /* Inline, not a modal: this is the whole content of the page rather
           than a failure on top of something usable, and a dialog the reader
           dismisses would leave them on a blank screen. */
        setRefused(
          error instanceof ApiRequestError
            ? error.message
            : 'This invite could not be read. Try the link again shortly.',
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!token || !preview) return;

      const next: Record<string, string> = {};
      if (password.length < preview.passwordMinimum) {
        next['password'] = `At least ${preview.passwordMinimum} characters — this account can move money.`;
      }
      if (confirm !== password) next['confirm'] = 'The two passwords do not match.';
      if (code.trim().length !== 6) next['code'] = 'Enter the 6-digit code from your authenticator app.';
      setErrors(next);
      if (Object.keys(next).length > 0) return;

      setBusy(true);
      try {
        const result = await api.post<{
          displayName: string;
          role: string;
          recoveryCodes: string[];
        }>('/api/auth/invite/accept', { token, password, code: code.trim() });
        setDone(result);
      } catch (error: unknown) {
        if (error instanceof ApiRequestError && error.fields) setErrors(error.fields);
        showError({
          title: 'The invite was not accepted',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'Nothing was set. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        setBusy(false);
      }
    },
    [token, preview, password, confirm, code, showError],
  );

  return (
    <div className="signin">
      <div className="signin-brand">
        <div className="signin-lockup">
          <BrandMark size={128} />
          <div className="signin-wordmark">
            NorthAlley<span className="signin-wordmark-accent">OPS</span>
          </div>
          <span className="signin-chip">B2B casino platform</span>
        </div>
        <div className="signin-pitch">
          <h1>Set up your account</h1>
          <p className="text-muted">
            One link, used once: it sets your password and enrols your authenticator together, so
            your second factor is in place before you ever sign in.
          </p>
        </div>
        <div className="signin-footnote text-subtle">
          <span>Session recorded for audit</span>
          <span>All figures in PHP · Asia/Manila</span>
        </div>
      </div>

      <div className="signin-panel">
        <div className="signin-form">
          {done ? (
            <div className="stack signin-step">
              <div className="signin-check">
                <Icon name="shield-check" />
              </div>
              <div>
                <h3>Account ready</h3>
                <div className="text-subtle signin-sub">
                  {done.displayName} · {done.role}. Save these recovery codes, then sign in.
                </div>
              </div>

              <div className="notice" role="alert">
                <Icon name="warning-circle" />
                <div>
                  <strong>Shown once.</strong> The platform stores only their digests, so nobody —
                  not a Super Admin, not this screen — can show them to you again. Each works
                  exactly once in place of a code from your app.
                </div>
              </div>

              <ul className="secret-codes">
                {done.recoveryCodes.map((c) => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ul>

              <a className="btn btn-primary btn-block" href="/">
                Go to sign in
                <Icon name="arrow-right" />
              </a>
            </div>
          ) : loading ? (
            <div className="skeleton" style={{ height: '240px' }}>
              &nbsp;
            </div>
          ) : refused || !preview ? (
            <div className="stack signin-step">
              <div>
                <h3>This invite cannot be used</h3>
                <div className="text-subtle signin-sub">Nothing has been changed on any account.</div>
              </div>
              <div className="notice" role="alert">
                <Icon name="warning-circle" />
                <div>{refused}</div>
              </div>
              <div className="notice notice-quiet">
                <div>
                  Invite links are single-use and expire 72 hours after they are generated, and
                  generating a new one revokes the old. Ask the admin who invited you to issue a
                  fresh one; they can send it or hand it over directly.
                </div>
              </div>
              <a className="btn btn-block" href="/">
                Go to sign in
              </a>
            </div>
          ) : (
            <form className="stack signin-step" onSubmit={submit} noValidate>
              <div>
                <h3>Welcome, {preview.displayName}</h3>
                <div className="text-subtle signin-sub">
                  {preview.email} · <Tag tone="accent">{preview.role}</Tag>
                </div>
              </div>

              <div className="notice notice-quiet">
                <Icon name="info" />
                <div>
                  A Super Admin created this account for {preview.email}, and this link is what
                  turns it into one you can sign in to — however it reached you. It works once,
                  and it sets both your password and your authenticator.
                  {preview.roleRequiresTwoFactor
                    ? ` ${preview.role} carries a capability the platform honours only from a session verified with two-factor authentication, so the app is not optional for you.`
                    : ' Your role does not mandate a second factor, but this platform enrols one for everybody — a password alone is not a credential worth having on an operator console.'}
                </div>
              </div>

              <Field
                label="Choose a password"
                hint={`At least ${preview.passwordMinimum} characters. Stored with argon2id, never in plain text.`}
                error={errors['password']}
              >
                {({ id, describedBy, invalid }) => (
                  <div className="signin-password">
                    <input
                      id={id}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      required
                    />
                    <button
                      type="button"
                      className="signin-eye"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                    >
                      <Icon name={showPassword ? 'eye-slash' : 'eye'} />
                    </button>
                  </div>
                )}
              </Field>

              <Field label="Type it again" error={errors['confirm']}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    required
                  />
                )}
              </Field>

              <Field
                label="Open in your authenticator app"
                hint="On a phone this opens the app directly. There is no QR code: drawing one would mean adding a barcode library, and this link carries exactly what a QR would encode."
              >
                {({ id }) => (
                  <a id={id} className="secret-block" href={preview.twoFactor.uri}>
                    {preview.twoFactor.uri}
                  </a>
                )}
              </Field>

              <Field
                label="Or type this secret in by hand"
                hint="Every authenticator app accepts a typed secret. Time-based, 6 digits, 30 seconds."
              >
                {({ id }) => (
                  <code id={id} className="secret-block">
                    {preview.twoFactor.secret.replace(/(.{4})/g, '$1 ').trim()}
                  </code>
                )}
              </Field>

              <Field label="Code from the app" error={errors['code']}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Type the 6-digit code"
                    className="signin-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                  />
                )}
              </Field>

              <Button variant="primary" block type="submit" busy={busy}>
                Set my password and enrol
                <Icon name="arrow-right" />
              </Button>

              <div className="signin-policy text-subtle">
                Accepting does not sign you in — you will sign in normally afterwards, which is
                what records the attempt and starts the lockout counter that protects it.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
